import { isDefined, isNotDefined } from '@sgme/fp';
import type { ChangeEvent } from 'react';
import { Form } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { useAlgoTileId, useValidateAlgo } from '@/App/tiles/orders/AlgoOrders/AlgoContext.tsx';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectAlgoErrors,
  selectOrderEntryByIdFromBlotter,
  selectTradeIdFromAlgoTile,
} from '@/store/orders/algoOrders/selectors.ts';
import { CLIPPING_MODES, type ClippingMode } from '@/store/orders/algoOrders/state.ts';

type AlgoClippingModeProps = {
  clippingMode: ClippingMode | undefined;
  setClippingMode: (value: ClippingMode | undefined) => void;
};

export const AlgoEditingClippingMode = (props: AlgoClippingModeProps) => {
  const { clippingMode, setClippingMode } = props;
  const tileId = useAlgoTileId();
  const track = useTrackEvent();

  const tradeId = useAppSelector((state) => selectTradeIdFromAlgoTile(state, tileId));
  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );
  const value =
    clippingMode ?? (isDefined(orderFromBlotter?.clippingMode) ? orderFromBlotter?.clippingMode : undefined);
  const errors = useAppSelector((state) => selectAlgoErrors(state, tileId));

  const ClippingModeError = errors?.clippingMode;

  const validateAlgo = useValidateAlgo();

  const handleClippingModeChange = async (event: ChangeEvent<HTMLSelectElement>) => {
    track('Order action', 'Clipping mode changed');
    const newClippingMode = event.target.value as ClippingMode;
    if (isDefined(orderFromBlotter)) {
      const [currency1, currency2] = isDefined(orderFromBlotter?.currencyPair)
        ? orderFromBlotter.currencyPair.split('/')
        : ['', ''];

      if (isNotDefined(currency1) || isNotDefined(currency2)) {
        console.error(`[handleClippingModeChange] Invalid currency pair: ${currency1}/${currency2}`);
        return;
      }

      const response = await validateAlgo({
        clippingMode: newClippingMode,
      });

      if (response?.data?.validationResponseDetailContent.length === 0) {
        setClippingMode(newClippingMode);
      }
    }
  };

  return (
    <OrderRow labelId="order.fields.clippingMode">
      <OmsFieldErrorWrapper errors={ClippingModeError}>
        <Form.Select value={value} onChange={handleClippingModeChange} size="sm" data-e2e="clipping-mode-select">
          {CLIPPING_MODES.map((clippingMode) => (
            <option key={clippingMode} value={clippingMode}>
              <FormattedMessage id={`order.fields.clippingMode.${clippingMode}`} />
            </option>
          ))}
        </Form.Select>
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
